import React, { useState } from 'react';
import { usePageTitle, Avatar } from '../components/AppShell';
import { useLang } from '../lib/lang';
import { type AuthUser } from '../lib/auth';
import { T } from '../lib/tokens';

interface Props { user: AuthUser }

interface Child {
  id:             string;
  fullName:       string;
  ic:             string; // masked
  dob:            string;
  school:         string;
  currentClass:   string;
  academicYear:   string;
  enrolledDate:   string;
  status:         string;
  email:          string; // used as avatar seed
}

const CHILDREN: Child[] = [
  {
    id: 'c1',
    fullName:     'Ahmad Zulfikri bin Hj Nordin',
    ic:           'XXX-XXXX-6712',
    dob:          '12 Julai 2010',
    school:       'SMAS Sayyidina Ali',
    currentClass: 'Tingkatan 4A',
    academicYear: '2025/2026',
    enrolledDate: '3 Januari 2020',
    status:       'Aktif',
    email:        'ahmad@smas.edu.bn',
  },
  {
    id: 'c2',
    fullName:     'Nurul Izzati binti Hj Nordin',
    ic:           'XXX-XXXX-8834',
    dob:          '5 November 2013',
    school:       'SMK Rimba',
    currentClass: 'Tingkatan 1B',
    academicYear: '2025/2026',
    enrolledDate: '4 Januari 2023',
    status:       'Aktif',
    email:        'izzati@smkrimba.edu.bn',
  },
];

const FIELD_ROWS: { labelMs: string; labelEn: string; key: keyof Child }[] = [
  { labelMs: 'Nama Penuh',          labelEn: 'Full Name',         key: 'fullName'     },
  { labelMs: 'No. IC (tersembunyi)',labelEn: 'IC No. (masked)',   key: 'ic'           },
  { labelMs: 'Tarikh Lahir',        labelEn: 'Date of Birth',     key: 'dob'          },
  { labelMs: 'Sekolah',             labelEn: 'School',            key: 'school'       },
  { labelMs: 'Kelas Semasa',        labelEn: 'Current Class',     key: 'currentClass' },
  { labelMs: 'Tahun Akademik',      labelEn: 'Academic Year',     key: 'academicYear' },
  { labelMs: 'Tarikh Pendaftaran',  labelEn: 'Enrolled Date',     key: 'enrolledDate' },
  { labelMs: 'Status',              labelEn: 'Status',            key: 'status'       },
];

export function ParentProfilPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === 'en';
  usePageTitle(E ? 'Child Profile' : 'Profil Anak');

  const [selected, setSelected] = useState('c1');
  const child = CHILDREN.find(c => c.id === selected)!;

  return (
    <div style={{ maxWidth: '720px' }}>
      <div className="page-header">
        <div>
          <h1 style={{ fontFamily: T.font.serif, fontSize: '24px', fontWeight: 700, color: T.color.textPrimary, marginBottom: '4px' }}>
            {E ? 'Child Profile' : 'Profil Anak'}
          </h1>
          <p style={{ fontSize: '13px', color: T.color.textSecondary }}>
            {E ? 'View-only — contact the school office to update information' : 'Paparan sahaja — hubungi pejabat sekolah untuk mengemas kini maklumat'}
          </p>
        </div>
        <span className="badge" style={{ background: '#F3F4F6', color: '#4A5568', fontSize: '11px' }}>
          🔒 {E ? 'Read Only' : 'Paparan Sahaja'}
        </span>
      </div>

      {/* Child selector */}
      {CHILDREN.length > 1 && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
          {CHILDREN.map(c => (
            <button
              key={c.id}
              className={`pill-filter${selected === c.id ? ' active' : ''}`}
              onClick={() => setSelected(c.id)}
            >
              {c.fullName.split(' ')[0]}
            </button>
          ))}
        </div>
      )}

      {/* Profile card */}
      <div style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #0F1117 0%, #1E2128 100%)', padding: '28px 32px', display: 'flex', alignItems: 'center', gap: '20px' }}>
          <Avatar email={child.email} size={64} />
          <div>
            <div style={{ fontFamily: T.font.serif, fontSize: '20px', fontWeight: 700, color: '#FFFFFF', marginBottom: '4px' }}>
              {child.fullName}
            </div>
            <div style={{ fontSize: '13px', color: '#9CA3AF' }}>{child.currentClass} · {child.school}</div>
            <div style={{ marginTop: '8px' }}>
              <span className="badge" style={{ background: T.color.successLight, color: T.color.success }}>
                ● {child.status}
              </span>
            </div>
          </div>
        </div>

        {/* Details grid */}
        <div style={{ padding: '0' }}>
          {FIELD_ROWS.map((f, i) => {
            const val = child[f.key] as string;
            const isLast = i === FIELD_ROWS.length - 1;
            return (
              <div key={f.key} style={{
                display: 'grid', gridTemplateColumns: '200px 1fr',
                padding: '14px 28px',
                borderBottom: isLast ? 'none' : '1px solid #F3F4F6',
                alignItems: 'center',
              }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: T.color.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {E ? f.labelEn : f.labelMs}
                </span>
                <span style={{ fontSize: '14px', color: T.color.textPrimary, fontWeight: f.key === 'fullName' ? 600 : 400 }}>
                  {f.key === 'status'
                    ? <span className="badge" style={{ background: T.color.successLight, color: T.color.success }}>{val}</span>
                    : val}
                </span>
              </div>
            );
          })}
        </div>

        {/* Redacted notice */}
        <div style={{ padding: '16px 28px', background: '#FFFBEB', borderTop: '1px solid #FCD34D' }}>
          <p style={{ fontSize: '12px', color: '#92400E', margin: 0 }}>
            {E
              ? 'Some information (health records, SEN details, internal remarks) is only visible to authorised school staff.'
              : 'Sesetengah maklumat (rekod kesihatan, butiran SEN, catatan dalaman) hanya boleh dilihat oleh kakitangan sekolah yang diberi kuasa.'}
          </p>
        </div>
      </div>
    </div>
  );
}
